import type { User } from '@/types/user'
import {
  hasActiveTeam,
  hasOrganization,
  isOrgLeader,
  isTeamLeader,
  isTeamMember,
  isTPAAdmin,
} from './user'

// Resolve the post-login / fallback destination for a user.
//
// Ordering matters: each role check returns a route the user is guaranteed to
// pass the guard for, so this value can be used safely as a denial redirect
// without risking an infinite loop. In particular we never return
// `create-organization` here - org creation is tpa_admin-only, and tpa_admins
// are routed to the dashboard above that point, so a non-admin can never be
// bounced toward a route they'd be denied from.
export function getUserHomePath(user: User | null): any {
  if (!user || !user.roles) {
    return { name: 'signin' }
  }

  if (isTPAAdmin(user)) {
    return { name: 'dashboard' }
  }

  if (isTeamLeader(user)) {
    if (hasActiveTeam(user)) {
      return { name: 'tl-dashboard' }
    }
    // No activated team yet → land on a team this user leads. Prefer a
    // team_leader membership, then any membership. Only route to
    // team-profile when a real id is resolvable - pushing `{ id: undefined }`
    // makes vue-router reject the navigation and leaves the user stuck on the
    // "Redirecting…" screen.
    const leaderTeamId =
      user.teamUsers?.find((tu) => tu.role === 'team_leader')?.teamId ??
      user.teamUsers?.[0]?.teamId
    if (leaderTeamId) {
      return { name: 'team-profile', params: { id: leaderTeamId } }
    }
    // else fall through to the org-leader / home fallbacks below.
  }

  if (isTeamMember(user)) {
    return { name: 'realizar-test' }
  }

  // Org leaders with no team land on their own organization's detail view
  // (which they're allowed to open via requiresAdminTPAorOrgLeader).
  if (isOrgLeader(user) && hasOrganization(user) && user.organization) {
    return { name: 'organization-profile', params: { id: user.organization.id } }
  }

  // Anyone else (incl. a user without an organization) has no in-app landing
  // surface - orgs are provisioned by an admin, not self-service.
  return { name: 'home' }
}
